<template>
  <DefaultLayout>
    <div :class="[$style.mainContainer, 'container jbi-criteria-container']">
      <div class="columns">
        <div class="column is-three-quarters" :class="$style.leftContainer">
          <div :class="$style.backContainer">
            <a @click="handleAttemptCloseParent">
              <b-icon
                :class="$style.backArrow"
                icon="arrow-left"
                size="is-small"
              />
              {{ pageTitle }}
            </a>
          </div>

          <div :class="$style.headerContainer" class="padding-top">
            <h1 :class="$style.heroBar">Add Criteria</h1>
            <p>You can add from JBI Criteria or create own custom criteria</p>
          </div>
          <div class="columns no-bottom-margin">
            <div class="column is-9 padding-bottom-none padding-right-none">
              <b-field>
                <b-input
                  rounded
                  expanded
                  type="search"
                  icon="magnify"
                  v-model="searchCriteriaTerm"
                  placeholder="Search by keywords or tags"
                  @keyup.enter.native="submitSearchResult"
                  class="search-bar-z-index"
                />
                <p class="control">
                  <b-button
                    expanded
                    :class="$style.searchSubmitButton"
                    rounded
                    @click="submitSearchResult"
                    type="is-primary"
                  >
                    Search
                  </b-button>
                </p>
              </b-field>
              <div v-if="displayResultCountText">
                {{ displayResultCountText }}
              </div>
            </div>
            <div class="column is-3 has-text-right padding-none">
              <div :class="$style.addItem">
                <a @click="openCriteriaAddModal">Create Custom Criteria</a>
              </div>
            </div>
          </div>

          <div
            v-if="!isProjectLevel && filteredProjectCriteria.length"
            :class="$style.criteriaListContainer"
          >
            <p style="font-size: 18px; font-weight: bold; padding: 10px 0">
              From Project
            </p>

            <div
              :class="$style.criteriaContainer"
              v-for="(criteria, cIndex) in displayedProjectCriteria"
              :key="cIndex"
            >
              <div class="columns" style="margin: 0">
                <div
                  class="column is-1 has-text-centered display-flex padding-right-none width-auto"
                >
                  <b-checkbox
                    :class="$style.criteriaCheckbox"
                    @input="selectedProjectCriteria(criteria)"
                    :native-value="criteria.criteriaDetails.id"
                    v-model="projectCriteriaModel"
                  />
                </div>
                <div>
                  <a
                    v-if="criteria.document.id"
                    :class="['text-size padding-left20', $style.documentLink]"
                    target="_blank"
                    :href="`/documents/${criteria.document.id}`"
                  >
                    {{ criteria.document.id }}
                    <b-icon
                      size="is-small"
                      pack="fas"
                      icon="external-link-alt"
                    />
                  </a>
                  <CriteriaDetailsTemplate
                    :criteria="criteria"
                    :activeSearchTerm="activeSearchTerm"
                  />
                </div>
              </div>
            </div>
          </div>

          <div :class="$style.criteriaListContainer">
            <p style="font-size: 18px; font-weight: bold; padding: 10px 0">
              From C++
            </p>
            <div
              :class="
                currentDocumentModel.indexOf(criterionObj.document.id) === -1
                  ? $style.groupedCriteriaListContainer
                  : $style.groupedCriteriaSelectedListContainer
              "
              v-for="(criterionObj, oIndex) in groupedCriteriaList"
              :key="oIndex"
            >
              <b-checkbox
                :class="['text-size', $style.groupDocumentIdCheckbox]"
                v-model="currentDocumentModel"
                :native-value="criterionObj.document.id"
                @input="changeSelectedDocumentCriteria(criterionObj)"
                :disabled="
                  checkIfAllDocumentCriteriaIsAlreadyAdded(criterionObj)
                "
              >
                <a
                  v-if="criterionObj.document.id"
                  :class="['text-size', $style.documentLink]"
                  target="_blank"
                  :href="`/documents/${criterionObj.document.id}`"
                >
                  {{ criterionObj.document.id }}
                  <b-icon size="is-small" pack="fas" icon="external-link-alt" />
                </a>
              </b-checkbox>
              <a
                :class="$style.collapsibleBtn"
                @click="groupedCriteriaTriggerHandler(oIndex)"
              >
                <b-icon
                  :icon="
                    groupedCriteriaCollapsed.indexOf(oIndex) !== -1
                      ? 'chevron-up'
                      : 'chevron-down'
                  "
                />
              </a>
              <div
                :class="['text-size', $style.documentTitle]"
                v-html="getHighlightedText(criterionObj.document.title)"
              ></div>
              <b-collapse
                :class="$style.collapsibleContainer"
                animation="slide"
                :key="oIndex"
                :open="groupedCriteriaCollapsed.indexOf(oIndex) !== -1"
              >
                <div
                  :class="$style.criteriaContainer"
                  v-for="(criteria, cIndex) in criterionObj.criteria"
                  :key="cIndex"
                >
                  <div class="columns">
                    <div
                      class="column is-1 has-text-centered display-flex padding-right-none width-auto"
                    >
                      <b-checkbox
                        :class="$style.criteriaCheckbox"
                        @input="changeSelectedCriteria(criteria)"
                        :native-value="criteria.id"
                        v-model="currentCriteriaModel"
                        :disabled="checkIfAlreadyAddedCriteria(criteria)"
                      />
                    </div>
                    <CriteriaDetailsTemplate
                      :criteria="criteria"
                      :activeSearchTerm="activeSearchTerm"
                    />
                    <div class="column is-1 has-text-right">
                      <AuditCriteriaDetailModal :criteria="criteria" />
                    </div>
                  </div>
                </div>
              </b-collapse>
            </div>
            <BasePagination
              v-if="groupedCriteriaList.length > 0"
              :page.sync="getCurrentPage"
              :perPage.sync="getCurrentPerPage"
              :totalNumberOfPage="totalNumberOfPage"
              :totalCount="totalCount"
              :isFirstPage="isFirstPage"
              :isLastPage="isLastPage"
              :startItemIndex="startItemIndex"
              :endItemIndex="endItemIndex"
            >
            </BasePagination>
          </div>
        </div>

        <div class="column" :class="$style.rightContainer">
          <div v-if="isNoCriteriaSelected" :class="$style.emptyRightContainer">
            No data collection period criteria selected yet.
          </div>
          <div v-else>
            <div
              :class="$style.rightHeaderContainer"
              v-if="
                currentGroupedCriteriaSelection.length > 0 ||
                customCriteriaList.length > 0
              "
            >
              <h1 :class="$style.rightHeader">
                Selected Criteria ({{ totalSelectedCriteria }})
              </h1>
            </div>
            <!-- start existing criteria list  -->
            <!-- Existing JBI criteria list -->
            <div
              :class="$style.criteriaViewGroupContainer"
              v-for="(criterionObj, oIndex) in currentGroupedCriteriaSelection"
              :key="oIndex"
            >
              <label>{{ criterionObj.documentId }}</label>
              <div :class="$style.criteriaViewListContainer">
                <div
                  class="columns"
                  :class="['text-size', $style.criteriaViewContainer]"
                  v-for="(criteria, cIndex) in criterionObj.criterias"
                  :key="cIndex"
                >
                  <div class="column is-1 padding-selected-criteria-text">
                    <span>{{ criteria.count }}.</span>
                  </div>
                  <div class="column padding-selected-criteria-text">
                    <div>{{ criteria.title }}</div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Existing custom criteria list -->
            <div
              v-if="customCriteriaList.length > 0"
              :class="$style.criteriaViewGroupCustomContainer"
            >
              <label>Custom Criteria</label>
              <div :class="$style.criteriaViewListContainer">
                <div
                  class="columns"
                  :class="['text-size', $style.criteriaViewContainer]"
                  v-for="(criteria, cindex) in customCriteriaList"
                  :key="cindex"
                >
                  <div class="column is-1 padding-selected-criteria-text">
                    <span>
                      <span>{{ criteria.count }}.</span>
                    </span>
                  </div>
                  <div class="column padding-selected-criteria-text">
                    <div>{{ criteria.title }}</div>
                  </div>
                </div>
              </div>
            </div>
            <!-- end existing criteria list  -->

            <!-- start new criteria list  -->
            <div
              v-if="
                newGroupedCriteriaSelection.length > 0 ||
                newCustomCriteriaList.length > 0
              "
            >
              <hr />
              <h1 :class="$style.rightHeader">
                New Selected Criteria ({{ totalNewSelectedCriteria }})
              </h1>

              <!-- New JBI criteria list -->
              <div
                :class="$style.criteriaViewGroupContainer"
                v-for="(newCriterionObj, index) in newGroupedCriteriaSelection"
                :key="`new-criterion-${index}`"
              >
                <label>{{ newCriterionObj.documentId }}</label>
                <div :class="$style.criteriaViewListContainer">
                  <div
                    class="columns"
                    :class="['text-size', $style.criteriaViewContainer]"
                    v-for="(criteria, cIndex) in newCriterionObj.criterias"
                    :key="`new-criteria-${cIndex}`"
                  >
                    <div class="column is-1 padding-selected-criteria-text">
                      <span>{{ criteria.count }}.</span>
                    </div>
                    <div class="column padding-selected-criteria-text">
                      <a
                        @click="handleRemoveSelectedCriteria(criteria)"
                        :class="$style.removeCriteriaBtn"
                        v-if="!checkIfAlreadyAddedCriteria(criteria)"
                      >
                        <b-icon icon="close" size="is-small"></b-icon>
                      </a>
                      <div>{{ criteria.title }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- New custom criteria list -->
              <div
                v-if="newCustomCriteriaList.length > 0"
                :class="$style.criteriaViewGroupCustomContainer"
              >
                <label>New Custom Criteria</label>
                <div :class="$style.criteriaViewListContainer">
                  <div
                    class="columns"
                    :class="['text-size', $style.criteriaViewContainer]"
                    v-for="(criteria, Index) in newCustomCriteriaList"
                    :key="Index"
                  >
                    <div class="column is-1 padding-selected-criteria-text">
                      <span> {{ criteria.count }}. </span>
                    </div>
                    <div class="column padding-selected-criteria-text">
                      <a
                        @click="
                          handleRemoveSelectedCriteria(criteria, true, Index)
                        "
                        :class="$style.removeCriteriaBtn"
                      >
                        <b-icon icon="close" size="is-small" />
                      </a>
                      <div>{{ criteria.title }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- end new criteria list  -->
          </div>

          <!-- Only render on project level -->
          <AddProjectCriteriaIntoAuditCycle
            v-if="shouldDisplayAuditSelection"
            :selectedAuditCycleIds.sync="selectedAuditIds"
            @selectedAuditCycles="handleSelectedAuditCycles"
          />

          <div
            v-if="!isNoCriteriaSelected"
            class="has-text-centered"
            :class="$style.continueBtnContainer"
          >
            <b-button
              @click="emitResult"
              :class="$style.continueBtn"
              type="is-primary"
              size="is-large"
            >
              Continue
            </b-button>
          </div>
        </div>
      </div>
      <AuditCriteriaCreateModal
        v-if="isCreateModalActive && createModalCriterion"
        :key="createModalKey"
        :criterion="createModalCriterion"
        modalTitle="Create Custom Criteria"
        :reOrderedCriteriaArray="currentCriteriaSelection"
        :isProject="isProjectLevel"
        :isModalActive="isCreateModalActive"
        :isMinimized="isCreateModalMinimized"
        :createProject="isCreateProject"
        @close="handleCloseCreateModal"
        @minimize="handleMinimizeCreateModal"
        @restore="handleRestoreCreateModal"
        @newCriteria="handleCreateCriteria"
      />
    </div>
  </DefaultLayout>
</template>
<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  CplusCriteria,
  CplusCriteriaItem,
  CplusCriteriaResults,
  GetCplusCriteriaRequestPayload,
  GroupedCriterias,
  JBICriterion,
} from '@/store/modules/projects/types/projects.types';
// TODO: remove lodash, and use alternative methods.
import { chain as _chain, difference as _difference } from 'lodash';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import AuditCriteriaDetailModal from '@/views/Project/components/AuditCriteriaDetailModal.vue';
import BasePagination from '@/components/base/BasePagination.vue';
import { Action, namespace, State } from 'vuex-class';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import { Route } from 'vue-router';
import AddProjectCriteriaIntoAuditCycle from '@/views/Project/components/AddProjectCriteriaIntoAuditCycle.vue';
import CriteriaDetailsTemplate from './CriteriaDetailsTemplate.vue';
import { showUnsavedChangesDialog } from '@/utils/dialog.util';
import AuditCriteriaCreateModal from '@/views/Project/components/AuditCriteriaCreateModal.vue';
import { createDefaultCriterion } from '@/views/Project/utils/default-criterion.util';
import { Criterion } from '@/store/types/criterions.types';
import { ToastProgrammatic as Toast } from 'buefy';
import { highlightText } from '@/utils/highlight.util';

const unsavedChangesGuard = namespace('unsavedChangesGuard');

@Component({
  components: {
    DefaultLayout,
    AuditCriteriaDetailModal,
    BasePagination,
    AddProjectCriteriaIntoAuditCycle,
    CriteriaDetailsTemplate,
    AuditCriteriaCreateModal,
  },
})
export default class JBICriteriaSelection extends Vue {
  @Prop() public filteredProjectCriteria!: CplusCriteria[];
  @Prop() public projectName!: string;
  @Prop() public auditName!: string;
  @Prop({ default: false, type: Boolean }) public isProjectLevel!: boolean;
  @Prop({ default: false, type: Boolean }) public isCreateProject!: boolean;
  @Prop() public selectedCriteriaArray!: CplusCriteria[];
  @Prop({ type: Boolean, default: false }) preSelectedCustomCriteria!: boolean;

  public searchCriteriaTerm: string = '';
  public activeSearchTerm: string = '';
  public totalCount: number = 0;
  public perPage = 50;
  public page = 1;

  public currentCriteriaSelection: CplusCriteria[] = [];
  public currentCriteriaModel: string[] = [];
  public projectCriteriaModel: string[] = [];
  public groupedCriteriaList: CplusCriteriaItem[] = [];
  public displayResultCountText: string = '';
  public currentDocumentModel: string[] = [];
  public currentGroupedCriteriaSelection: any = [];
  public newGroupedCriteriaSelection: any = [];
  public groupedCriteriaCollapsed: number[] = [];
  public customCriteriaList: any[] = [];
  public newCustomCriteriaList: any[] = [];
  public lastOrder: number = 0;
  public deletedCriteriaIds: number[] = [];
  public selectedAuditCycleIds: number[] = [];

  public isCreateModalActive: boolean = false;
  public isCreateModalMinimized: boolean = false;
  public createModalCriterion: Criterion | null = null;
  public displayedProjectCriteria: CplusCriteria[] = [];

  private createModalKey: number = 0;

  @Action('projects/getCplusCriterias')
  public getCplusCriterias!: (params: GetCplusCriteriaRequestPayload) => void;

  @State((state) => state.projects.cplusCriterias)
  public cplusCriterias!: CplusCriteriaResults;

  @unsavedChangesGuard.State('userSelectedKeepEditing')
  public userSelectedKeepEditing!: boolean;

  @unsavedChangesGuard.Action('resetKeepEditingFlag')
  public resetKeepEditingFlag!: () => void;

  get pageTitle(): string {
    return this.isProjectLevel || this.isCreateProject
      ? this.projectName
        ? this.projectName
        : `Project Creation`
      : this.auditName;
  }

  get totalNumberOfPage(): number {
    return Math.ceil(this.totalCount / this.perPage);
  }

  get startItemIndex(): number {
    return this.page * this.perPage - this.perPage + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.totalCount, this.page * this.perPage);
  }

  get isFirstPage(): boolean {
    return this.page === 1;
  }

  get isLastPage(): boolean {
    return this.page === this.totalNumberOfPage;
  }

  get getCurrentPage(): number {
    return this.page;
  }

  set getCurrentPage(newValue: number) {
    this.page = Number(newValue);
    this.getFilteredCplusCriterias();
  }

  get getCurrentPerPage(): number {
    return this.perPage;
  }

  set getCurrentPerPage(newValue: number) {
    this.perPage = Number(newValue);
    this.getFilteredCplusCriterias();
  }

  get isNoCriteriaSelected(): boolean {
    return (
      this.customCriteriaList.length === 0 &&
      this.currentGroupedCriteriaSelection.length === 0 &&
      this.newGroupedCriteriaSelection.length === 0 &&
      this.newCustomCriteriaList.length === 0
    );
  }

  get selectedAuditIds() {
    return this.selectedAuditCycleIds;
  }

  set selectedAuditIds(newValue: number[]) {
    this.selectedAuditCycleIds = newValue;
  }

  get shouldDisplayAuditSelection(): boolean {
    return (
      !this.isCreateProject &&
      this.isProjectLevel &&
      (this.newGroupedCriteriaSelection.length ||
        this.newCustomCriteriaList.length)
    );
  }

  get totalSelectedCriteria(): number {
    let totalCriteria = 0;

    if (this.currentGroupedCriteriaSelection.length > 0) {
      totalCriteria = this.currentGroupedCriteriaSelection
        .map(({ criterias }: GroupedCriterias) => criterias.length)
        .reduce(
          (accumulator: number, currentValue: number) =>
            accumulator + currentValue,
        );
    }
    return this.customCriteriaList.length + totalCriteria;
  }

  get totalNewSelectedCriteria(): number {
    let totalCriteria = 0;
    if (this.newGroupedCriteriaSelection.length > 0) {
      totalCriteria = this.newGroupedCriteriaSelection
        .map(({ criterias }: GroupedCriterias) => criterias.length)
        .reduce(
          (accumulator: number, currentValue: number) =>
            accumulator + currentValue,
        );
    }
    return this.newCustomCriteriaList.length + totalCriteria;
  }

  // Generate a unique identifier for the criteria to ensure distinct tracking or reference
  public generateUniqueCriteriaId() {
    return `criteria_${Math.floor(Math.random() * 999)}`;
  }

  public getFilteredCplusCriterias() {
    this.getCplusCriterias({
      search: this.searchCriteriaTerm,
      page: this.page,
      perPage: this.perPage,
    });
  }

  public mounted(): void {
    this.displayedProjectCriteria = this.filteredProjectCriteria;
    this.populateData();
    this.getFilteredCplusCriterias();
  }

  public populateData(): void {
    this.selectedCriteriaArray.forEach((criteria) => {
      if (
        criteria.document &&
        criteria.document.id !== '' &&
        !criteria.isChanged
      ) {
        this.currentCriteriaSelection.push({
          ...criteria,
          uniqueCriteriaId: !criteria.uniqueCriteriaId
            ? this.generateUniqueCriteriaId()
            : criteria.uniqueCriteriaId,
        });
      } else {
        this.customCriteriaList.push({
          ...criteria,
          uniqueCriteriaId: !criteria.uniqueCriteriaId
            ? this.generateUniqueCriteriaId()
            : criteria.uniqueCriteriaId,
        });
      }

      this.lastOrder =
        criteria.order > this.lastOrder ? criteria.order : this.lastOrder;

      this.updateGroupedCriteriaSelection();
    });
  }

  public selectedProjectCriteria(projectCriteria: CplusCriteria) {
    const isCustomCriteria =
      !projectCriteria.document.id || projectCriteria.isChanged;
    const selectedJBICriteriaId: string = String(
      projectCriteria.criteriaDetails.connectPlusId,
    );
    const selectedJBICriteriaDocumentId: string = String(
      projectCriteria.document.id,
    );

    let existingCriteriaInSelectedList: any = undefined;
    if (isCustomCriteria) {
      existingCriteriaInSelectedList = this.newCustomCriteriaList.find(
        (currentNewCriteria) => {
          return currentNewCriteria.id === projectCriteria.criteriaDetails.id;
        },
      );
    } else {
      existingCriteriaInSelectedList = this.currentCriteriaSelection.find(
        (currentCriteria) => {
          const currentCriteriaId =
            currentCriteria.criteriaDetails && !currentCriteria.isChanged
              ? currentCriteria.criteriaDetails.connectPlusId
              : currentCriteria.id;
          return (
            currentCriteriaId === selectedJBICriteriaId &&
            currentCriteria.document.id === selectedJBICriteriaDocumentId
          );
        },
      );
    }

    /**
     * if criteria exist in selected list("currentCriteriaSelection"),
     * then it's been removed
     * else it's been added
     */
    if (existingCriteriaInSelectedList) {
      if (isCustomCriteria) {
        this.newCustomCriteriaList = this.newCustomCriteriaList.filter(
          (newCriteria) => newCriteria.id !== existingCriteriaInSelectedList.id,
        );
      } else {
        /** Sync currentCriteriaModel(CPlus Criteria) with ProjectCriteria */
        const index = this.currentCriteriaModel.indexOf(
          projectCriteria.criteriaDetails.connectPlusId,
          0,
        );
        if (index > -1) {
          this.currentCriteriaModel.splice(index, 1);
        }
        this.removeSelectedCriteria(existingCriteriaInSelectedList);
      }
    } else {
      /** Sync currentCriteriaModel(CPlus Criteria) with ProjectCriteria */
      const cPlusDocument = this.groupedCriteriaList.find(
        (criteriaDocument) =>
          criteriaDocument.document.id === projectCriteria.document.id,
      );

      const isCplusCriteria = cPlusDocument
        ? cPlusDocument.criteria.find(
            (criteria) =>
              criteria.id === projectCriteria.criteriaDetails.connectPlusId,
          )
        : undefined;

      if (isCplusCriteria) {
        this.currentCriteriaModel.push(
          projectCriteria.criteriaDetails.connectPlusId,
        );
      }

      if (isCustomCriteria) {
        this.newCustomCriteriaList.push({
          ...projectCriteria,
          isNewCriteria: true,
          isProjectLevelCriteria: true,
          projectCriteriaId: +projectCriteria.criteriaDetails.id,
          uniqueCriteriaId: !projectCriteria.uniqueCriteriaId
            ? this.generateUniqueCriteriaId()
            : projectCriteria.uniqueCriteriaId,
        });
      } else {
        this.currentCriteriaSelection.push({
          ...projectCriteria,
          isNewCriteria: true,
          isProjectLevelCriteria: true,
          projectCriteriaId: +projectCriteria.criteriaDetails.id,
          uniqueCriteriaId: !projectCriteria.uniqueCriteriaId
            ? this.generateUniqueCriteriaId()
            : projectCriteria.uniqueCriteriaId,
        });
      }
    }

    this.updateDocumentModel(selectedJBICriteriaDocumentId);
    this.updateGroupedCriteriaSelection();
  }

  public changeSelectedCriteria(selectedCriteria: JBICriterion): void {
    const selectedCriteriaId: string = selectedCriteria.id;
    const selectedCriteriaDocumentId: string =
      selectedCriteria.document.id || '';

    const existingCriteriaInSelectedList = this.currentCriteriaSelection.find(
      (currentCriteria) => {
        const currentCriteriaId =
          currentCriteria.criteriaDetails && !currentCriteria.isChanged
            ? +currentCriteria.criteriaDetails.connectPlusId
            : +currentCriteria.id;

        return (
          currentCriteriaId === +selectedCriteria.id &&
          currentCriteria.document.id === selectedCriteria.document.id
        );
      },
    );

    /**
     * if criteria exist in selected list("currentCriteriaSelection"),
     * then it's been removed
     * else it's been added
     */

    if (existingCriteriaInSelectedList) {
      this.syncProjectCriteria(existingCriteriaInSelectedList);
      this.removeSelectedCriteria(existingCriteriaInSelectedList);
    } else {
      /** Sync projectCriteriaModel(Project Criteria) with CPlus Criteria*/
      const isProjectCriteria: CplusCriteria | undefined = !this.isProjectLevel
        ? this.filteredProjectCriteria.find(
            (pc) =>
              !pc.criteriaDetails.isChanged &&
              pc.criteriaDetails.connectPlusId === selectedCriteriaId,
          )
        : undefined;

      if (isProjectCriteria) {
        this.projectCriteriaModel.push(isProjectCriteria.id);
      }

      /** check if it's re-added criteria */
      const existingCriteria = this.selectedCriteriaArray.find(
        (existingSelectedCriteria) => {
          const tempCriteria =
            existingSelectedCriteria.criteriaDetails &&
            !existingSelectedCriteria.isChanged;

          if (!tempCriteria) {
            return false;
          }

          const existingSelectedCriteriaId = tempCriteria
            ? existingSelectedCriteria.criteriaDetails.connectPlusId
            : existingSelectedCriteria.id;

          const existingSelectedCriteriaDocumentId =
            existingSelectedCriteria.document.id;

          return (
            existingSelectedCriteriaId === selectedCriteriaId &&
            existingSelectedCriteriaDocumentId === selectedCriteriaDocumentId
          );
        },
      );

      const isProjectLevelCriteria = isProjectCriteria ? true : false;
      const projectCriteriaId = isProjectCriteria
        ? +isProjectCriteria.criteriaDetails.id
        : undefined;

      if (existingCriteria) {
        this.removeSelectedCriteria(existingCriteria);
        this.currentCriteriaSelection.push({
          ...existingCriteria,
          isNewCriteria: false,
          isProjectLevelCriteria,
          projectCriteriaId,
          uniqueCriteriaId: !existingCriteria.uniqueCriteriaId
            ? this.generateUniqueCriteriaId()
            : existingCriteria.uniqueCriteriaId,
        });
      } else {
        this.currentCriteriaSelection.push({
          ...selectedCriteria,
          isNewCriteria: true,
          isProjectLevelCriteria,
          projectCriteriaId,
          uniqueCriteriaId: this.generateUniqueCriteriaId(),
        } as any);
      }
    }

    this.updateDocumentModel(selectedCriteriaDocumentId);
    this.updateGroupedCriteriaSelection();
  }

  public handleRemoveSelectedCriteria(
    selectedCriteria: CplusCriteria,
    isCustomCriteria: boolean = false,
    criteriaIndex: number = 0,
  ) {
    if (isCustomCriteria) {
      // Check if it's from newCustomCriteriaList first
      if (
        this.newCustomCriteriaList.length > 0 &&
        this.newCustomCriteriaList[criteriaIndex] === selectedCriteria
      ) {
        this.newCustomCriteriaList.splice(criteriaIndex, 1);
      } else {
        // Existing logic for customCriteriaList
        if (selectedCriteria.id) {
          this.deletedCriteriaIds.push(Number(selectedCriteria.id));
        }
        this.customCriteriaList.splice(criteriaIndex, 1);
      }
    } else {
      // Existing logic for removing non-custom criteria
      const selectedCriteriaId: string =
        selectedCriteria.criteriaDetails && !selectedCriteria.isChanged
          ? selectedCriteria.criteriaDetails.connectPlusId
          : selectedCriteria.id;

      const cPlusDocument = this.groupedCriteriaList.find(
        (criteriaDocument) =>
          criteriaDocument.document.id === selectedCriteria.document.id,
      );

      /** if CPlusDocument exists, remove from currentCriteriaModel */
      if (cPlusDocument) {
        const index = this.currentCriteriaModel.indexOf(selectedCriteriaId, 0);
        if (index > -1) {
          this.currentCriteriaModel.splice(index, 1);
        }
      }

      this.syncProjectCriteria(selectedCriteria);
      this.removeSelectedCriteria(selectedCriteria);
      this.updateDocumentModel(selectedCriteria.document.id);
      this.updateGroupedCriteriaSelection();
    }
  }

  public removeSelectedCriteria(selectedCriteria: any) {
    const selectedCriteriaId: string = selectedCriteria.criteriaDetails
      ? selectedCriteria.criteriaDetails.id
      : selectedCriteria.id;

    /**
     * criteria has criteriaDetails and doesn't already added into deletedList,
     * then add into deletedCriteriaIds
     *
     * Note: criteria with criteriaDetails is already saved criteria,
     * so we need passed delete payload to backend api to marked it as deleted.
     */
    if (
      selectedCriteriaId &&
      !this.deletedCriteriaIds.includes(Number(selectedCriteriaId))
    ) {
      this.deletedCriteriaIds.push(Number(selectedCriteriaId));
    } else {
      const index = this.deletedCriteriaIds.indexOf(+selectedCriteriaId, 0);
      if (index > -1) {
        this.deletedCriteriaIds.splice(index, 1);
      }
    }

    /** Filter criteria - removed match criteria */
    this.currentCriteriaSelection = this.currentCriteriaSelection.filter(
      (currentCriteria) => {
        const currentCriteriaId = currentCriteria.criteriaDetails
          ? currentCriteria.criteriaDetails.id
          : currentCriteria.id;

        /** match criteria based on criteriaId and documentID */
        const isCriteriaMatched: boolean =
          currentCriteriaId === selectedCriteriaId &&
          currentCriteria.document.id === selectedCriteria.document.id;

        /** return criteria, which doesn't match */
        if (!isCriteriaMatched) {
          return true;
        }
      },
    );
  }

  public updateCriteriaModel(): void {
    this.selectedCriteriaArray.forEach((criteria) => {
      const criteriaId: string =
        criteria.criteriaDetails && !criteria.isChanged
          ? criteria.criteriaDetails.connectPlusId
          : criteria.id;

      if (
        !this.currentCriteriaModel.includes(criteriaId) &&
        criteria.document &&
        criteria.document.id !== ''
      ) {
        const cPlusDocument = this.groupedCriteriaList.find(
          (criteriaDocument) =>
            criteriaDocument.document.id === criteria.document.id,
        );

        /** if CPlusDocument exists, add into currentCriteriaModel */
        if (
          cPlusDocument &&
          cPlusDocument.document.id === criteria.document.id
        ) {
          this.currentCriteriaModel.push(criteriaId);
        }

        this.updateDocumentModel(criteria.document.id);
        this.updateGroupedCriteriaSelection();
      }
    });
  }

  public updateCplusCriteriaDisplayResult() {
    /** Set document collapsed empty */
    this.groupedCriteriaCollapsed = [];
    this.groupedCriteriaList = this.cplusCriterias.items.map(
      (criteriaItem: CplusCriteriaItem) => {
        const criterias = criteriaItem.criteria.map(
          (criteria: JBICriterion) => {
            return {
              ...criteria,
              document: criteriaItem.document,
            };
          },
        );

        return {
          documentId: criteriaItem.document.id,
          document: criteriaItem.document,
          criteria: criterias,
        };
      },
    );

    this.updateCriteriaModel();

    this.groupedCriteriaList.forEach((criteriaDocument: CplusCriteriaItem) => {
      this.updateDocumentModel(criteriaDocument.documentId);
    });

    if (this.searchCriteriaTerm) {
      this.displayResultCountText =
        this.cplusCriterias.total_count +
        ` Results for '` +
        this.searchCriteriaTerm +
        `'`;
    } else {
      this.displayResultCountText = '';
    }

    // this.groupedCriteriaCollapsed = [];
  }

  /**
   * This method used to group criteria based on criteria DocumentId
   * and add serial number as count for each criteria
   *
   * Order should be:
   * 1. Existing Criteria (currentGroupedCriteriaSelection)
   * 2. Existing Custom Criteria (customCriteriaList)
   * 3. New Criteria (newGroupedCriteriaSelection)
   * 4. Existing Custom Criteria (newCustomCriteriaList)
   */
  public updateGroupedCriteriaSelection() {
    let count = 0;
    /** start grouping and numbering existing criteria */
    // Filters existing criteria: has criteriaDetails and documentId
    let currentCriteria = this.currentCriteriaSelection.filter(
      (criteria) =>
        criteria.criteriaDetails &&
        criteria.document.id &&
        !criteria.isProjectLevelCriteria &&
        !criteria.isChanged,
    );

    // Always update currentGroupedCriteriaSelection with existing criteria
    if (currentCriteria.length > 0) {
      const groupedSelection = _chain(currentCriteria)
        .groupBy('document.id')
        .map((value, key) => {
          value.forEach((o) => {
            count++;
            o.count = count;
          });
          return { documentId: key, criterias: value };
        })
        .value();

      this.currentGroupedCriteriaSelection = groupedSelection;
    } else {
      // Reset currentGroupedCriteriaSelection if no current criteria exist
      this.currentGroupedCriteriaSelection = [];
    }
    /** end grouping and numbering existing criteria */

    /** start numbering existing custom criteria */
    if (this.customCriteriaList.length > 0) {
      this.customCriteriaList = this.customCriteriaList.map(
        (customCriteria) => {
          count++;
          return { ...customCriteria, count };
        },
      );
    } else {
      this.customCriteriaList = [];
    }
    /** end numbering existing custom criteria */

    /** start grouping and numbering new criteria */
    // Filters new criteria: does not have criteriaDetails but has documentId
    let newCriteria = this.currentCriteriaSelection.filter(
      (criteria) =>
        (!criteria.criteriaDetails && criteria.document.id) ||
        criteria.isProjectLevelCriteria,
    );

    // Reset newGroupedCriteriaSelection if no new criteria exist
    if (newCriteria.length === 0) {
      this.newGroupedCriteriaSelection = [];
    }

    // If there are new criteria, group them
    if (newCriteria.length > 0) {
      const groupedSelection = _chain(newCriteria)
        .groupBy('document.id')
        .map((value, key) => {
          value.forEach((o) => {
            count++;
            o.count = count;
          });
          return { documentId: key, criterias: value };
        })
        .value();

      this.newGroupedCriteriaSelection = groupedSelection;
    }
    /** end grouping and numbering new criteria */

    /** start numbering new custom criteria */
    if (this.newCustomCriteriaList.length > 0) {
      this.newCustomCriteriaList = this.newCustomCriteriaList.map(
        (criteria) => {
          count++;
          return { ...criteria, count };
        },
      );
    } else {
      this.newCustomCriteriaList = [];
    }
    /** end numbering new custom criteria */
  }

  public checkIfAllDocumentCriteriaIsAlreadyAdded(
    updatedGroupedCriterias: CplusCriteriaItem,
  ): boolean {
    const isDocumentModelAlreadySelected: boolean = this.currentDocumentModel.includes(
      updatedGroupedCriterias.documentId,
    );

    if (!isDocumentModelAlreadySelected) {
      return false;
    }

    const isAnyDocumentCriteriaAlreadyAdded = updatedGroupedCriterias.criteria.some(
      (criteria) => this.checkIfAlreadyAddedCriteria(criteria),
    );

    return isAnyDocumentCriteriaAlreadyAdded;
  }

  /**
   * @param criteria
   *
   * @description
   * This method checks if the given criteria is already added to the selectedCriteriaArray.
   * It compares the criteria passed with the criteria already added.
   */
  public checkIfAlreadyAddedCriteria(criteria: JBICriterion): boolean {
    // Checks the source of the criteria
    const idToCheck = this.getCriteriaSource(criteria);

    // Check if the criteria is already added
    const isAlreadyAdded = this.selectedCriteriaArray.some(
      (selectedCriteria) => {
        const selectedId = this.getCriteriaSource(selectedCriteria);
        return selectedId === idToCheck;
      },
    );

    return isAlreadyAdded;
  }

  /**
   * @param criterion
   * Partial<CplusCriteria> since we only use document, criteriaDetails, id, and uniqueCriteriaId
   * @description
   * This method is used to determine the source of criteria
   * 1. Check if the criterion is added from CPLUS but not yet added to PACES
   * 2. If criterion has criteriaDetails, means it has already been added to PACES.
   *    If it doesn't have criteriaDetails, it is a new criterion from CPLUS.
   * 3. If the first check doesn't pass then check for 'uniqueCriteriaId'
   * since it is the only unique identifier for a custom criterion.
   * 4. Finally, check if the criterion is a saved custom criterion
   */
  public getCriteriaSource(criterion: Partial<CplusCriteria>): string | null {
    // Guard against null/undefined input
    if (!criterion) {
      return null;
    }

    // For a saved criterion, the source ID is in 'criteriaDetails.connectPlusId'.
    // For a new criterion from CPLUS, the source ID is in the top-level 'id'.
    if (criterion.document && criterion.document.id) {
      const sourceId =
        (criterion.criteriaDetails &&
          criterion.criteriaDetails.connectPlusId) ||
        criterion.id;
      return `cplus_${criterion.document.id}_${sourceId}`;
    }

    // Only way to check if it is a custom criterion is by checking 'uniqueCriteriaId'.
    if (criterion.uniqueCriteriaId) {
      return `custom_${criterion.uniqueCriteriaId}`;
    }

    // A custom criterion that has been saved has id but no document.
    // We explicitly check that 'document' or 'document.id' is missing.
    if (
      typeof criterion.id === 'number' &&
      (!criterion.document || !criterion.document.id)
    ) {
      return `custom_${criterion.id}`;
    }

    console.warn('Could not determine source for criterion:', criterion);
    return null;
  }

  public changeSelectedDocumentCriteria(
    updatedGroupedCriterias: CplusCriteriaItem,
  ) {
    if (
      this.currentDocumentModel.includes(updatedGroupedCriterias.document.id)
    ) {
      // Add all criteria by document
      updatedGroupedCriterias.criteria.forEach((criteria) => {
        if (!this.currentCriteriaModel.includes(criteria.id)) {
          this.currentCriteriaModel.push(criteria.id);
          this.changeSelectedCriteria(criteria);
        }
      });
    } else {
      // Remove all criteria by document
      updatedGroupedCriterias.criteria.forEach((criteria) => {
        const index = this.currentCriteriaModel.indexOf(criteria.id, 0);
        if (index > -1) {
          this.currentCriteriaModel.splice(index, 1);
        }

        this.syncProjectCriteria(criteria);
        this.changeSelectedCriteria(criteria);
      });
    }
  }

  public updateDocumentModel(documentId: string) {
    const cPlusDocument = this.groupedCriteriaList.find(
      (criteriaDocument) => criteriaDocument.document.id === documentId,
    );

    if (cPlusDocument) {
      const criteriaIdList: string[] = cPlusDocument.criteria.map(
        (criteria: JBICriterion) => criteria.id,
      );
      if (_difference(criteriaIdList, this.currentCriteriaModel).length === 0) {
        if (!this.currentDocumentModel.includes(documentId)) {
          this.currentDocumentModel.push(documentId);
        }
      } else {
        const index = this.currentDocumentModel.indexOf(documentId, 0);
        if (index > -1) {
          this.currentDocumentModel.splice(index, 1);
        }
      }
    }
  }

  /** Sync projectCriteriaModel(Project Criteria) with CPlus Criteria*/
  public syncProjectCriteria(criteria: CplusCriteria | JBICriterion) {
    if (!this.isProjectLevel) {
      const selectedCriteriaId: string =
        (criteria as CplusCriteria).criteriaDetails &&
        !(criteria as CplusCriteria).isChanged
          ? (criteria as CplusCriteria).criteriaDetails.connectPlusId
          : criteria.id;

      const isProjectCriteria: CplusCriteria | undefined = !this.isProjectLevel
        ? this.filteredProjectCriteria.find(
            (pc) =>
              !pc.criteriaDetails.isChanged &&
              pc.criteriaDetails.connectPlusId === selectedCriteriaId,
          )
        : undefined;

      if (isProjectCriteria) {
        const projectCriteriaIndex = this.projectCriteriaModel.indexOf(
          isProjectCriteria.id,
          0,
        );
        if (projectCriteriaIndex > -1) {
          this.projectCriteriaModel.splice(projectCriteriaIndex, 1);
        }
      }
    }
  }

  public handleSelectedAuditCycles(selectedAuditIds: number[]) {
    this.selectedAuditCycleIds = selectedAuditIds;
  }

  public emitResult() {
    let criteriaOrder: number = 0;

    // Assign order to existing criteria if not already set
    for (const criteria of this.currentCriteriaSelection) {
      criteriaOrder++;
      // If order is already set, keep it; otherwise, assign the new order
      criteria.order = criteria.order ? criteria.order : criteriaOrder;
    }

    // Assign order to existing custom criteria if not already set
    for (const criteria of this.customCriteriaList) {
      criteriaOrder++;
      // If order is already set, keep it; otherwise, assign the new order
      criteria.order = criteria.order ? criteria.order : criteriaOrder;
    }

    // Assign order to new custom criteria
    for (const criteria of this.newCustomCriteriaList) {
      criteriaOrder++;
      // New custom criteria always get a new order
      criteria.order = criteriaOrder;
    }

    let emitCriteriaArray = [
      ...this.currentCriteriaSelection,
      ...this.customCriteriaList,
      ...this.newCustomCriteriaList,
    ];

    // Sort by criteria.order before emitting
    emitCriteriaArray.sort((a, b) => a.order - b.order);

    // After emitting, clear the new custom criteria list
    this.newCustomCriteriaList = [];

    const existingCriteriaId: number[] = [];
    this.selectedCriteriaArray.forEach((criteria) => {
      if (criteria.criteriaDetails) {
        existingCriteriaId.push(criteria.criteriaDetails.id);
      }
    });

    this.$emit('updateSelectedCriteriaArray', {
      emitCriteriaArray,
      deletedCriteriaIds: this.deletedCriteriaIds.filter((id) =>
        existingCriteriaId.includes(id),
      ),
      selectedAuditCycleIds: this.selectedAuditCycleIds,
    });
    this.$emit('close');
  }

  public groupedCriteriaTriggerHandler(index: number) {
    const searchIndex = this.groupedCriteriaCollapsed.indexOf(index);
    if (searchIndex === -1) {
      this.groupedCriteriaCollapsed.push(index);
    } else {
      this.groupedCriteriaCollapsed.splice(searchIndex, 1);
    }
  }

  public openCriteriaAddModal() {
    this.createModalCriterion = createDefaultCriterion();
    this.isCreateModalActive = true;
    this.isCreateModalMinimized = false;
  }

  public handleCloseCreateModal(): void {
    this.isCreateModalActive = false;
    this.isCreateModalMinimized = false;
    this.createModalCriterion = null;
    this.createModalKey += 1; // Force re-mount of modal to reset its state

    if (this.preSelectedCustomCriteria) {
      this.$emit('close');
    }
  }

  public handleMinimizeCreateModal(): void {
    this.isCreateModalMinimized = true;
  }

  public handleRestoreCreateModal(): void {
    this.isCreateModalMinimized = false;
  }

  public handleCreateCriteria(newCriteria: CplusCriteria): void {
    this.newCustomCriteriaList.push({
      ...newCriteria,
      isChanged: false,
      isCustom: true,
      isNewCriteria: true,
      uniqueCriteriaId: this.generateUniqueCriteriaId(),
    });
    this.updateGroupedCriteriaSelection();

    Toast.open({
      message: 'Custom criteria successfully created',
      position: 'is-top',
      type: 'is-dark',
      duration: 3500,
    });
    // After creating, we can close the modal.
    this.handleCloseCreateModal();
  }

  public async handleAttemptCloseParent(): Promise<void> {
    // If the create modal is not active, close the parent immediately.
    if (!this.isCreateModalActive) {
      this.$emit('close');
      return;
    }

    // If the modal is active, show the confirmation dialog.
    const userConfirmed = await showUnsavedChangesDialog();
    if (userConfirmed) {
      // User chose to discard changes. Close everything.
      this.handleCloseCreateModal(); // First, clean up the create modal state
      this.$emit('close'); // Then, close the parent component
    } else {
      // User chose "Keep Editing". If the modal was minimized, restore it.
      if (this.isCreateModalMinimized) {
        this.handleRestoreCreateModal();
      }
    }
  }

  public compareSimilarElementsInObjects(arr1: any[], arr2: any[]) {
    arr1.sort((a, b) => b.id - a.id);
    arr2.sort((a, b) => b.id - a.id);

    // If the elements are not similar in both objects return false and vise versa
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) {
        return false;
      }
    }
    return true;
  }

  public submitSearchResult(): void {
    this.activeSearchTerm = this.searchCriteriaTerm.trim();

    // Only perform client-side search if we are in an audit context.
    if (!this.isProjectLevel) {
      const lowerCaseSearchTerm = this.searchCriteriaTerm.toLowerCase().trim();
      if (lowerCaseSearchTerm === '') {
        // If search is empty, show all project criteria
        this.displayedProjectCriteria = this.filteredProjectCriteria;
      } else {
        // If search has a term, filter the original list
        this.displayedProjectCriteria = this.filteredProjectCriteria.filter(
          (criteria) => {
            const title = criteria.criteriaDetails.title;
            return title.toLowerCase().includes(lowerCaseSearchTerm);
          },
        );
      }
    }

    // --- Search logic for "From C++" criteria
    this.page = 1;
    this.getFilteredCplusCriterias();
  }

  public getHighlightedText(text: string): string {
    return highlightText(text, this.activeSearchTerm);
  }

  @Watch('cplusCriterias')
  @isTruthy
  public modifyCplusCriteriaResult(newValue: CplusCriteriaResults): void {
    this.totalCount = newValue.total_count;
    this.updateCplusCriteriaDisplayResult();
  }

  @Watch('$route', { immediate: true, deep: true })
  public onUrlChange(newValue: Route): void {
    if (
      newValue.name !== 'project-create' &&
      newValue.name !== 'project-draft' &&
      newValue.name !== 'project-details' &&
      newValue.name !== 'audit'
    ) {
      this.$emit('close');
    }
  }

  @Watch('userSelectedKeepEditing')
  public onUserSelectedKeepEditing(newValue: boolean): void {
    if (newValue && this.isCreateModalActive && this.isCreateModalMinimized) {
      this.handleRestoreCreateModal();
    }
    // Always reset the flag after checking it
    this.resetKeepEditingFlag();
  }
}
</script>
<style lang="scss" module>
.backContainer a {
  color: #444f51;
  font-size: 20px;
  font-weight: 400;
}

.leftContainer {
  padding-right: 84px;
  min-height: 100%;
  border-right: 1px solid #c0d4e5;
}

.heroBar {
  color: #00205b;
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 5px;
  line-height: 38px;
}

.headerContainer {
  padding-top: 34px;
  padding-bottom: 40px;

  p {
    color: #444f51;
  }
}

.searchSubmitButton {
  height: 40px;
  border-radius: 24px !important;
  margin-left: -15px;
  z-index: 20;
}

.searchSubmitButton:hover {
  z-index: 20 !important;
}

.criteriaListContainer {
  padding-top: 48px;
}

.groupedCriteriaListContainer {
  padding: 16px 16px 16px 19px;
  background-color: #ffffff;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  margin-bottom: 15px;
}

.groupedCriteriaSelectedListContainer {
  padding: 16px 16px 16px 19px;
  background-color: #f7fdff;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  margin-bottom: 15px;

  .criteriaContainer {
    background-color: #f7fdff;
  }
}

.criteriaHeader {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.groupDocumentIdCheckbox {
  color: #21b5ea;
  font-weight: 400;
}

.criteriaContainer {
  background-color: #ffffff;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  padding: 16px 20px 14px 16px;
  margin-top: 8px;
}

.rightContainer {
  padding: 0 0 1rem 2.5rem;
}

.rightHeaderContainer {
  padding-top: 2rem;
}

.rightHeader {
  color: #444f51;
  font-size: 18px;
  font-weight: 700;
  line-height: 22px;
}

.criteriaViewGroupContainer {
  padding: 24px 0;

  label {
    font-size: 14px;
    line-height: 17px;
  }
}

.criteriaViewGroupCustomContainer {
  padding: 14px 0 24px 0;

  label {
    font-size: 14px;
    line-height: 17px;
  }
}

.criteriaViewContainer {
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  margin-top: 5px;
  background-color: #fff;
  padding: 16px 12px 16px 12px;
  color: #444f51;
}

.criteriaViewListContainer {
  padding: 0 1rem;
}

.removeCriteriaBtn {
  position: relative;
  float: right;

  p {
    font-size: 1rem;
    font-weight: 400;
  }

  i {
    padding-top: 50%;
    color: $grey-3;
    font-size: 20px;
  }
}

.continueBtn {
  background-color: #00205b;
  border-radius: 24px;
  box-shadow: 0 2px 4px 0 rgba(29, 112, 184, 0.33);
  width: 123px;
  height: 48px;
  font-size: 18px !important;
  line-height: 22px !important;
  padding: 20px;
}

.collapsibleBtn {
  float: right;
  color: #21b5ea;
}

.collapsibleContainer,
.documentTitle {
  padding-left: 25px;
}

.documentTitle {
  color: #444f51;
  font-weight: 700;
  padding-top: 4px;
}

.addItem {
  color: #00205b;
  line-height: 3.7;
  padding-right: 0.75rem;
}

.addItem > a {
  color: #00205b;
  font-weight: 700;
  font-size: 14px;
  line-height: 17px;
}

.emptyRightContainer {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  color: #9f9f9f;
  font-size: 14px;
  line-height: 17px;
  text-align: center;
}

.backArrow {
  margin-right: 22px;
}

.continueBtnContainer {
  margin-top: 28px;
}

.mainContainer {
  padding-top: 32px;
}

.documentLink {
  color: #21b5ea;
  font-size: 14px;
  font-weight: 700;
  padding-right: unset;
}
</style>
